// @flow
import * as React from 'react';
import {TrackingContext} from '@homeaway/react-event-tracking';
import {formatDate, convertDashesToSlashes} from '@homeaway/date-utils';
import {FormattedMessage} from 'react-intl';

import {GENERIC_EVENT} from '../../utils/edapWrapper';

import FlexibleDatesDesktop from './FlexibleDatesDesktop';
import FlexibleDatesMobile from './FlexibleDatesMobile';

function createDateObj(dateStr: string): Date {
    return new Date(convertDashesToSlashes(dateStr));
}

type onDateChangeWrapperArgs = {
    startDate: Date,
    endDate: Date,
    eventlabel: string
};

export type FlexibleDatesOptionProps = {
    days: number,
    id: string,
    label: string
};

export type FlexibleDatesOptionExtraProps = {
    startDate: Date,
    endDate: Date,
    loading: boolean,
    locale: string,
    onDateChange: (args: onDateChangeWrapperArgs) => void
};

export type FlexibleDatesOptions = {
    sub: Array<FlexibleDatesOptionProps>,
    add: Array<FlexibleDatesOptionProps>
};

type Props = {
    startDate: string,
    endDate: string,
    title: string,
    locale: string,
    loading: boolean,
    isDesktop: boolean,
    onDateChange: (arrival: string, departure: string) => void,
    options: FlexibleDatesOptions
};

export type FlexibleDatesDesktopMobileProps = FlexibleDatesOptionExtraProps & {
    title: React.Node,
    options: FlexibleDatesOptions
};

function FlexibleDates({
    startDate,
    endDate,
    title,
    onDateChange,
    isDesktop,
    ...props
}: Props) {
    // $FlowFixMe
    const [internalStartDate, setInternalStartDate] = React.useState(
        createDateObj(startDate)
    );
    // $FlowFixMe
    const [internalEndDate, setInternalEndDate] = React.useState(
        createDateObj(endDate)
    );

    // $FlowFixMe
    const trackingContext = React.useContext(TrackingContext);

    // Keep track of dates changed outsite the component
    // $FlowFixMe
    React.useEffect(() => {
        setInternalStartDate(createDateObj(startDate));
        setInternalEndDate(createDateObj(endDate));
    }, [startDate, endDate]);

    function onDateChangeWrapper({
        startDate: newStartDate,
        endDate: newEndDate,
        eventlabel
    }) {
        setInternalStartDate(newStartDate);
        setInternalEndDate(newEndDate);

        const arrival = formatDate(newStartDate, null, true);
        const departure = formatDate(newEndDate, null, true);

        trackingContext.trigger(GENERIC_EVENT, {
            eventaction: 'search',
            eventlabel
        });

        onDateChange(arrival, departure);
    }

    const titleTranslated = (
        <FormattedMessage id={title} defaultMessage={title} />
    );

    const commonProps = {
        ...props,
        startDate: internalStartDate,
        endDate: internalEndDate,
        onDateChange: onDateChangeWrapper,
        title: titleTranslated
    };

    return isDesktop ? (
        <FlexibleDatesDesktop {...commonProps} />
    ) : (
        <FlexibleDatesMobile {...commonProps} />
    );
}

FlexibleDates.defaultProps = {
    title: 'Flexible dates? See more options',
    loading: false,
    options: {
        sub: [
            {
                days: 7,
                label: '1 week earlier',
                id: 'OneWeekEarlier'
            },
            {
                days: 1,
                label: '1 day earlier',
                id: 'OneDayEarlier'
            }
        ],
        add: [
            {
                days: 1,
                label: '1 day later',
                id: 'OneDayLater'
            },
            {
                days: 7,
                label: '1 week later',
                id: 'OneWeekLater'
            }
        ]
    },
    locale: 'us'
};

export default FlexibleDates;
